import type { OpeningTrimType } from "building-types/casing/openingTrimTypes";
import { OpeningTrimTypes } from "building-types/casing/openingTrimTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { BuildingEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { ExteriorFinishEdgeType } from "building-types/exteriorFinishEdgeAttributeTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoomType } from "building-types/rooms/roomTypesEnum";
import type { RailingPostPlacementType, RailingRailType } from "building-types/settings/railingTypes";
import type { EntityDescriptor } from "building-types/views/viewEntityTypes";
import type { WallCut } from "building-types/walls/wallCutTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { FeetSquared, InchesSquared, WorldFeet, WorldInches } from "common/types/nominalNumbersTypes";
import type { FaceTagBase } from "geom/brep";

export enum BuildingRoofAnalyticConditions {
  ROOF = "ROOF",
  ROOFING = "ROOFING",
  RIDGE_HORIZONTAL = "RIDGE_HORIZONTAL",
  RIDGE_SLOPED = "RIDGE_ANGLED",
  RIDGE_VENT = "RIDGE_VENT",
  VALLEY = "VALLEY",
  FLASHING = "ROOF_FLASHING",
  EAVE = "EAVE",
  RAKE = "RAKE",
  SOFFIT = "SOFFIT",
  SOFFIT_LINE = "SOFFIT_LINE",
}

export enum BuildingSlopeConditions {
  HORIZONTAL = "HORIZONTAL",
  SLOPED = "SLOPED",
}

export type BuildingGeomTag =
  | BuildingGeomCondition
  | BuildingGeomPosition
  | BuildingGeomParent
  | BuildingGeomOperation
  | BuildingGeomAnalytic
  | BuildingGeomAttributeTag<BuildingGeomAttributeValueType>;

export type BuildingGeomTagType =
  | "CONDITION"
  | "POSITION"
  | "PARENT"
  | "OPERATION"
  | "ANALYTIC_CONDITION"
  | "MATERIAL"
  | "ATTRIBUTE";

export const INDEX_ATTRIBUTE = "index";

/**
 * geometric and operational conditions where Faces are generated
 */
export type ConditionType = CarpentryConditions | ClippingOperations | FaceMiscConditions | ArchitecturalCondition;

export type OperationType = FaceOperations | ClippingOperations;

export type FaceMiscConditions = "HOLE" | "EXTRACTED_FACE" | "FORCE_DOUBLE_SIDED";

/**
 * clipping operations
 * @todo break these up into more atomic tags (e.g. ROOF_TRIM becomes ROOF and TRIM)
 */
export type ClippingOperations =
  | "CLIPPED" // clipped in unknown context
  | "CLIP_EAVE_RETURN_ENTITIES"
  | "CLIPPED_ROOF_TRIM"
  | "CLIP_GABLE_SUB_ROOF"
  | "CLIP_WALL_SYSTEM"
  | "CLIP_EXTERIOR_FINISH";

/**
 * carpentry conditions where Faces are generated
 * @todo break these up into more atomic tags (e.g. ROOF_TRIM becomes ROOF and TRIM)
 */
export type CarpentryConditions =
  | "EAVE_TRIM"
  | "FRIEZE_BOARD_FROM_RAILS"
  | "SOFFIT_FACE"
  | "FASCIA_BOARD"
  | "BELLY_BAND_BOARD"
  | "EAVE_FRIEZE_BOARD"
  | "EXTRA_EAVE_FRIEZE_BOARD"
  | "CORNER_BOARD_PROFILE"
  | "WALL_BASE_BOARD"
  | "DORMER_WALL_BASE_BOARDS"
  | "TABLE_TRIM"
  | "ROOF_WALL_FACE"
  | "ROOF_TRIM"
  | "GABLE_FRIEZE_BOARD_ALONG_RAIL"
  | "GABLE_MINI_SHED_ROOF"
  | "GABLE_MINI_HIP_ROOF"
  | "GABLE_FULL_RETURN_HIP_ROOF"
  | "SOLID_MASS_FROM_WALL_ENDS"
  | "SOLID_WALL_CUT_FINISH_OPENING"
  | "DOOR_UNIT_ARC"
  | "JAM_BOARD"
  | "ROOF_RAFTER"
  | "FASCIA_TOP_TRIM"
  | "ROOF_RIDGE_CAP";

/**
 * specific system conditions not associated with carpentry conditions
 * @todo break these up into more atomic tags (e.g. ROOF_TRIM becomes ROOF and TRIM)
 */
export type FaceOperations =
  | "CAP_FINISH_FACE"
  | "WALL_FACE_FROM_SINGLE_FACE"
  | "TRIM_WALL_SYSTEM_WITH_HALF_STORY_ROOFS"
  | "GABLE_END_TRY_ADD_WINDOW_OPENING"
  | "TRIM_OPEN_WINDOW_UNIT"
  | "REBUILT_ROOF_WALL_FACE"
  | "WALL_CUT_30n_FACE"
  | "WALL_SECTION_CAP_FINISH_FACE"
  | "GABLE_END_FACADE"
  | "TRIM_WALL_SYSTEM_WITH_STAIRS"
  | "REPAIRED_STAIR_TRIMMED_WALL_FACE"
  | "EXTRUDED_ROOFING_MATERIAL"
  | "APPLY_WALL_SWEEPS_TO_WALL_SIDE"
  | "OPERATE_WALL_FACE"
  | "SPLIT_AND_ASSIGN_RECURSIVE";

export type ArchitecturalCondition = "GABLE";

/**
 * contextual positions of a Face within some other entity/assembly
 */
export type FacePosition = "START" | "END" | "BOTTOM" | "TOP" | "LEFT" | "RIGHT" | "FRONT" | "BACK";

export type BuildingGeomParentType =
  | BuildingEntityType
  | "WALL_MASS"
  | "WALL_SUB_MASS"
  | GeneratedWindowPlacement["type"]
  | GeneratedDoorPlacement["type"]
  | WallCut["type"]
  | GeneratedRoof["roofType"]
  | EntityDescriptor["entityType"]
  | "LEVEL"
  | "PROFILE"
  | "EXTERIOR_OPENING_TRIM";

export interface BuildingGeomCondition extends FaceTagBase {
  readonly type: "CONDITION";
  readonly condition: ConditionType;
}

export interface BuildingGeomPosition extends FaceTagBase {
  readonly type: "POSITION";
  /**
   * does the Face have a meaningful geometric position?
   */
  readonly position: FacePosition;
}
/**
 * not necessarily a valid DocEntityHandle or
 * BuildingEntityHandle, as the Face may be generated by elements
 * for which handles have never been defined, like GableEndWalls
 */

export interface BuildingGeomParent extends FaceTagBase {
  readonly type: "PARENT";
  /**
   * parent entity active at the time of geometry generation
   * @todo: this should be a BuildingEntityHandle
   */
  readonly parentType: BuildingGeomParentType;
  readonly parentId: string;
}

export interface BuildingGeomOperation extends FaceTagBase {
  readonly type: "OPERATION";
  readonly operation: OperationType;
}

export const TAG_POSITION_START: BuildingGeomPosition = {
  type: "POSITION",
  position: "START",
};

export const TAG_POSITION_END: BuildingGeomPosition = {
  type: "POSITION",
  position: "END",
};

export const TAG_POSITION_BOTTOM: BuildingGeomPosition = {
  type: "POSITION",
  position: "BOTTOM",
};

export const TAG_POSITION_TOP: BuildingGeomPosition = {
  type: "POSITION",
  position: "TOP",
};

export const TAG_POSITION_LEFT: BuildingGeomPosition = {
  type: "POSITION",
  position: "LEFT",
};

export const TAG_POSITION_RIGHT: BuildingGeomPosition = {
  type: "POSITION",
  position: "RIGHT",
};

export const TAG_POSITION_FRONT: BuildingGeomPosition = {
  type: "POSITION",
  position: "FRONT",
};

export const TAG_POSITION_BACK: BuildingGeomPosition = {
  type: "POSITION",
  position: "BACK",
};

export const TAG_CONDITION_HOLE: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "HOLE",
};

export const TAG_CONDITION_GABLE: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "GABLE",
};

/**
 * Tag for faces that are created to force rendering as
 * double-sided. These should be ignored when calculating
 * area.
 */
export const TAG_CONDITION_FORCE_DOUBLE_SIDED: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "FORCE_DOUBLE_SIDED",
};

export const CLIP_EXTERIOR_FINISH: BuildingGeomOperation = {
  type: "OPERATION",
  operation: "CLIP_EXTERIOR_FINISH",
};

export const WALL_EXTERIOR_FINISH_TAG = "WALL_EXTERIOR_FINISH";
export const WALL_INTERIOR_FINISH_TAG = "WALL_INTERIOR_FINISH";

export const EXTERIOR_FINISH_TAG = buildingGeomAnalyticTagNew(WALL_EXTERIOR_FINISH_TAG);
export const INTERIOR_FINISH_TAG = buildingGeomAnalyticTagNew(WALL_INTERIOR_FINISH_TAG);

export const SOFFIT_TAG = "ROOF_SOFFIT";
export const SOFFIT_DEPTH_TAG = "SOFFIT_DEPTH";
export const SOFFIT_RAIL_TAG = "SOFFIT_RAIL";
export const SOFFIT = buildingGeomAnalyticTagNew(SOFFIT_TAG);
export const SOFFIT_DEPTH = buildingGeomAnalyticTagNew(SOFFIT_DEPTH_TAG);
export const SOFFIT_RAIL = buildingGeomAnalyticTagNew(SOFFIT_RAIL_TAG);

export const DOOR_TRIM_TAG = "DOOR_TRIM";
export const WINDOW_TRIM_TAG = "WINDOW_TRIM";

export const DOOR_TRIM = buildingGeomAnalyticTagNew(DOOR_TRIM_TAG);
export const WINDOW_TRIM = buildingGeomAnalyticTagNew(WINDOW_TRIM_TAG);

export const RAILING_TAG = "RAILING";
export const RAILING = buildingGeomAnalyticTagNew(RAILING_TAG);

export const RAIL_TAG = "RAIL";
export const RAIL = buildingGeomAnalyticTagNew(RAIL_TAG);

export const POST_TAG = "POST";
export const POST = buildingGeomAnalyticTagNew(POST_TAG);

export const BALUSTER_TAG = "BALUSTER";
export const BALUSTER = buildingGeomAnalyticTagNew(BALUSTER_TAG);

export const INFILL_TAG = "INFILL";
export const INFILL = buildingGeomAnalyticTagNew(INFILL_TAG);

export const SOLID_TAG = "SOLID";
export const SOLID = buildingGeomAnalyticTagNew(SOLID_TAG);

export const APRON = buildingGeomAnalyticTagNew(OpeningTrimTypes.APRON);
export const HEAD_CASING = buildingGeomAnalyticTagNew(OpeningTrimTypes.HEAD_CASING);
export const SIDE_CASING = buildingGeomAnalyticTagNew(OpeningTrimTypes.SIDE_CASING);
export const STOOL = buildingGeomAnalyticTagNew(OpeningTrimTypes.STOOL);
export const KEYSTONE = buildingGeomAnalyticTagNew(OpeningTrimTypes.KEYSTONE);

export const FLASHING = buildingGeomAnalyticTagNew(BuildingRoofAnalyticConditions.FLASHING);

export const SLOPED = buildingGeomAnalyticTagNew(BuildingSlopeConditions.SLOPED);
export const HORIZONTAL = buildingGeomAnalyticTagNew(BuildingSlopeConditions.HORIZONTAL);

export const FLOOR_STRUCTURE = buildingGeomAnalyticTagNew("FLOOR_STRUCTURE");

export const COLUMN_TAG = "COLUMN";
export const COLUMN = buildingGeomAnalyticTagNew(COLUMN_TAG);

export const WALL_DOMAIN_OPENING_INTERSECTION_TAG = "WALL_DOMAIN_OPENING_INTERSECTION";
export const WALL_DOMAIN_OPENING_INTERSECTION = buildingGeomAnalyticTagNew(WALL_DOMAIN_OPENING_INTERSECTION_TAG);

export const EDGE_TAG = "EDGE";
export const EDGE = buildingGeomAnalyticTagNew(EDGE_TAG);

export const DOOR_TAG = "DOOR";
export const DOOR = buildingGeomAnalyticTagNew(DOOR_TAG);
export const WINDOW_TAG = "WINDOW";
export const WINDOW = buildingGeomAnalyticTagNew(WINDOW_TAG);
export const OPEN_TAG = "OPEN";
export const OPEN = buildingGeomAnalyticTagNew(OPEN_TAG);
export const INTERIOR_TAG = "INTERIOR";
export const INTERIOR = buildingGeomAnalyticTagNew(INTERIOR_TAG);
export const EXTERIOR_TAG = "EXTERIOR";
export const EXTERIOR = buildingGeomAnalyticTagNew(EXTERIOR_TAG);

export function trimTypeTag(type: OpeningTrimType): BuildingGeomTag {
  switch (type) {
    case "APRON":
      return APRON;
    case "HEAD_CASING":
      return HEAD_CASING;
    case "SIDE_CASING":
      return SIDE_CASING;
    case "STOOL":
      return STOOL;
    case "KEYSTONE":
      return KEYSTONE;
    default:
      throw new Error(`Unknown trim type: ${type}`);
  }
}

export interface BuildingGeomAnalytic extends FaceTagBase {
  readonly type: "ANALYTIC_CONDITION";
  readonly condition: string;
}

export function buildingGeomAnalyticTagNew(condition: string): BuildingGeomAnalytic {
  return {
    type: "ANALYTIC_CONDITION",
    condition,
  };
}

export type BuildingGeomAttributeValueTypeMap = {
  ["STRING"]: string;
  ["NUMBER"]: number;
  ["FEET"]: WorldFeet;
  ["INCHES"]: WorldInches;
  ["FEET_SQUARED"]: FeetSquared;
  ["INCHES_SQUARED"]: InchesSquared;
  ["ROOM_TYPE"]: RoomType;
  ["EXTERIOR_FINISH_EDGE_TYPE"]: ExteriorFinishEdgeType;
  ["RAILING_POST_PLACEMENT_TYPE"]: RailingPostPlacementType;
  ["RAILING_RAIL_TYPE"]: RailingRailType;
};

export type BuildingGeomAttributeValueType = keyof BuildingGeomAttributeValueTypeMap;

export interface BuildingGeomAttributeTag<T extends BuildingGeomAttributeValueType = BuildingGeomAttributeValueType> {
  readonly type: "ATTRIBUTE";
  readonly attributeName: string;
  readonly valueType: T;
  readonly attributeValue: BuildingGeomAttributeValueTypeMap[T];
}

export function buildingGeomAttributeTagNew<T extends BuildingGeomAttributeValueType>(
  attributeName: string,
  attributeValue: BuildingGeomAttributeValueTypeMap[T],
  valueType: T,
): BuildingGeomAttributeTag<T> {
  return {
    type: "ATTRIBUTE",
    attributeName,
    attributeValue,
    valueType,
  };
}
